import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon, Loader } from "semantic-ui-react";
import { getUserInfo } from "../../../api/axios";
import { WebStorage, WebStorageNames } from "../../../utils";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import { fetchFullUserDetails } from "./userProfileApi";
import {
  clearProfileViewUserId,
  getProfileViewUserId,
  setProfileViewUserId,
} from "./profileViewStorage";
import {
  buildAdditionalProfile,
  buildLeaveSummary,
  buildUserProfileInfo,
  getActiveLeaveTypes,
  getAvatarColor,
} from "./userProfileData";
import "./UserProfilePage.css";

function resolveCurrentUserId() {
  const userInfo = getUserInfo();
  if (!userInfo) return null;
  return (
    userInfo.id ||
    userInfo.userId ||
    userInfo.Id ||
    userInfo.UserId ||
    userInfo.ID ||
    userInfo.userID ||
    null
  );
}

function isAdminRole() {
  try {
    const roleId = WebStorage.getLocalStore(WebStorageNames.RoleId) ?? localStorage.getItem("roleId");
    const n = parseInt(roleId, 10);
    return n === 1 || n === 2;
  } catch {
    return false;
  }
}

const ProfileDetail = ({ icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="user-profile-detail">
      <dt>
        <i className={`bi bi-${icon}`} aria-hidden /> {label}
      </dt>
      <dd>{value}</dd>
    </div>
  );
};

const UserProfilePage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const isSelfRoute = location.pathname === "/profile";

  const viewUserId = useMemo(() => {
    if (isSelfRoute) return null;
    const fromState = location.state?.userId;
    if (fromState != null && fromState !== "") return String(fromState);
    return getProfileViewUserId();
  }, [isSelfRoute, location.state?.userId]);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const currentUserId = useMemo(() => resolveCurrentUserId(), []);
  const isAdmin = useMemo(() => isAdminRole(), []);

  useEffect(() => {
    if (isSelfRoute) return;
    if (location.state?.userId) {
      setProfileViewUserId(location.state.userId);
    }
    if (!viewUserId) {
      history.replace("/users");
    }
  }, [isSelfRoute, location.state?.userId, viewUserId, history]);

  const loadProfile = useCallback(async () => {
    if (!isSelfRoute && !viewUserId) return;

    setLoading(true);
    setLoadError(false);
    const targetId = isSelfRoute ? undefined : viewUserId;
    const data = await fetchFullUserDetails(targetId);
    if (data) {
      setUser(data);
    } else {
      setUser(null);
      setLoadError(true);
    }
    setLoading(false);
  }, [isSelfRoute, viewUserId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const profile = useMemo(() => buildUserProfileInfo(user), [user]);
  const additionalProfile = useMemo(() => buildAdditionalProfile(user), [user]);
  const leaveSummary = useMemo(() => buildLeaveSummary(user), [user]);
  const leaveTypes = useMemo(() => getActiveLeaveTypes(user), [user]);
  const accentColor = useMemo(() => getAvatarColor(user || profile), [user, profile]);

  const isOwnProfile = useMemo(() => {
    if (isSelfRoute) return true;
    if (!profile?.id || !currentUserId) return false;
    return String(profile.id) === String(currentUserId);
  }, [isSelfRoute, profile?.id, currentUserId]);

  const canEdit = isOwnProfile || isAdmin;

  const handleBack = () => {
    if (!isSelfRoute) {
      clearProfileViewUserId();
    }
    if (location.state?.from) {
      history.push(location.state.from);
      return;
    }
    if (isSelfRoute) {
      history.push("/dashboard");
      return;
    }
    history.push("/users");
  };

  const handleEdit = () => {
    const editId = profile?.id || viewUserId;
    if (!editId) return;
    history.push({
      pathname: "/dashboard/user-form",
      state: { userId: editId, mode: "edit", from: location.pathname },
    });
  };

  if (!isSelfRoute && !viewUserId) {
    return null;
  }

  const pageTitle = isOwnProfile
    ? t("app.users.myProfileTitle", "My Profile")
    : t("app.users.profileTitle", "Employee Profile");

  const pageDescription = isOwnProfile
    ? t("app.users.myProfileDescription", "Your account, workplace, and leave balances at a glance.")
    : t("app.users.profileDescription", "Complete overview of employee information and leave entitlements.");

  if (loading) {
    return (
      <div className="user-profile-page container-fluid px-3 px-md-4 py-4">
        <div className="user-profile-loading">
          <Loader active inline="centered" size="large" />
          <p>{t("app.users.loadingProfile", "Loading profile…")}</p>
        </div>
      </div>
    );
  }

  if (loadError || !profile) {
    return (
      <div className="user-profile-page container-fluid px-3 px-md-4 py-4">
        <CommonPageHeader
          icon={<i className="bi bi-person-badge" />}
          title={pageTitle}
          description={t("app.users.profileNotFound", "We could not load this profile.")}
          onBack={handleBack}
          backLabel={t("app.users.backToUsers", "Back")}
        />
        <div className="user-profile-empty">
          <Icon name="user times" size="huge" />
          <p>{t("app.users.profileNotFound", "We could not load this profile.")}</p>
          <button type="button" className="user-profile-btn user-profile-btn--primary" onClick={loadProfile}>
            {t("app.users.tryAgain", "Try again")}
          </button>
        </div>
      </div>
    );
  }

  const usedPct =
    leaveSummary && leaveSummary.totalDaysAllocated > 0
      ? Math.min(100, Math.round((leaveSummary.daysUsed / leaveSummary.totalDaysAllocated) * 100))
      : 0;

  const ap = additionalProfile;
  const hasPersonalDetails = Boolean(
    ap &&
      (ap.employeeCode ||
        ap.gender ||
        ap.dateOfBirth ||
        ap.maritalStatus ||
        ap.fatherOrHusbandName ||
        ap.bloodGroup ||
        ap.disability)
  );
  const hasContactDetails = Boolean(
    ap && (ap.phoneNumber || ap.emergencyContactName || ap.emergencyContactNumber)
  );
  const hasIdentification = Boolean(ap && (ap.cnic || ap.passportNumber || ap.ntn));
  const hasAddress = Boolean(
    ap && (ap.residentialAddress || ap.permanentAddress || ap.city || ap.country || ap.postalCode)
  );
  const hasDependents = Boolean(ap && ap.dependents.length > 0);

  return (
    <div className="user-profile-page container-fluid px-3 px-md-4 py-4">
      <CommonPageHeader
        barClassName="user-profile-page-header"
        icon={<i className="bi bi-person-badge" />}
        title={pageTitle}
        description={pageDescription}
        onBack={handleBack}
        backLabel={
          isSelfRoute && !location.state?.from
            ? t("app.common.back", "Back")
            : t("app.users.backToUsers", "Back to Employees")
        }
        rightContent={
          canEdit ? (
            <button type="button" className="user-profile-btn user-profile-btn--primary" onClick={handleEdit}>
              <i className="bi bi-pencil-square" aria-hidden />
              {isOwnProfile
                ? t("app.users.editMyProfile", "Edit my profile")
                : t("app.users.editUserTitle", "Edit Employee")}
            </button>
          ) : null
        }
      />

      <section className="user-profile-hero" style={{ "--profile-accent": accentColor }}>
        <div className="user-profile-hero__glow" aria-hidden />
        <div className="user-profile-hero__inner">
          <div className="user-profile-hero__identity">
            <div className="user-profile-avatar-ring">
              {profile.imageURL ? (
                <img src={profile.imageURL} alt="" className="user-profile-avatar" />
              ) : (
                <div
                  className="user-profile-avatar user-profile-avatar--initials"
                  style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)` }}
                >
                  {profile.name?.charAt(0)?.toUpperCase() || "?"}
                </div>
              )}
            </div>
            <div className="user-profile-hero__text">
              <p className="user-profile-hero__eyebrow">
                {isOwnProfile
                  ? t("app.header.myDetails", "My details")
                  : t("app.users.profileEyebrow", "Team member")}
              </p>
              <h2 className="user-profile-hero__name">{profile.name}</h2>
              <a className="user-profile-hero__email" href={`mailto:${profile.email}`}>
                {profile.email}
              </a>
              <div className="user-profile-badges">
                <span className={`user-profile-badge user-profile-badge--${profile.isActive ? "active" : "inactive"}`}>
                  <i className={`bi bi-${profile.isActive ? "check-circle-fill" : "pause-circle-fill"}`} aria-hidden />
                  {profile.isActive ? t("app.users.active", "Active") : t("app.users.inactive", "Inactive")}
                </span>
                <span className="user-profile-badge">
                  <i className="bi bi-shield-check" aria-hidden />
                  {profile.role}
                </span>
                {profile.designation ? (
                  <span className="user-profile-badge">
                    <i className="bi bi-briefcase" aria-hidden />
                    {profile.designation}
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          {leaveSummary ? (
            <div className="user-profile-hero__usage">
              <div className="user-profile-usage-label">
                <span>{t("app.users.leaveUtilization", "Leave utilization")}</span>
                <strong>{usedPct}%</strong>
              </div>
              <div className="user-profile-usage-track">
                <div className="user-profile-usage-fill" style={{ width: `${usedPct}%` }} />
              </div>
              <p className="user-profile-usage-meta">
                {leaveSummary.daysUsed} {t("app.users.daysUsedShort", "used")} · {leaveSummary.daysRemaining}{" "}
                {t("app.users.daysRemainingShort", "remaining")} · {leaveSummary.totalDaysAllocated}{" "}
                {t("app.users.daysTotalShort", "total")}
              </p>
            </div>
          ) : null}
        </div>
      </section>

      {leaveSummary ? (
        <div className="user-profile-stats">
          <article className="user-profile-stat-card">
            <span className="user-profile-stat-card__icon user-profile-stat-card__icon--indigo">
              <i className="bi bi-layers" aria-hidden />
            </span>
            <div>
              <p className="user-profile-stat-card__value">{leaveSummary.activeLeaveTypesCount}</p>
              <p className="user-profile-stat-card__label">
                {t("app.users.statActiveLeaveTypes", "Active leave types")}
              </p>
            </div>
          </article>
          <article className="user-profile-stat-card">
            <span className="user-profile-stat-card__icon user-profile-stat-card__icon--emerald">
              <i className="bi bi-calendar-plus" aria-hidden />
            </span>
            <div>
              <p className="user-profile-stat-card__value">{leaveSummary.totalDaysAllocated}</p>
              <p className="user-profile-stat-card__label">
                {t("app.users.statTotalAllocated", "Days allocated")}
              </p>
            </div>
          </article>
          <article className="user-profile-stat-card">
            <span className="user-profile-stat-card__icon user-profile-stat-card__icon--rose">
              <i className="bi bi-calendar-check" aria-hidden />
            </span>
            <div>
              <p className="user-profile-stat-card__value">{leaveSummary.daysUsed}</p>
              <p className="user-profile-stat-card__label">{t("app.users.statDaysUsed", "Days used")}</p>
            </div>
          </article>
          <article className="user-profile-stat-card">
            <span className="user-profile-stat-card__icon user-profile-stat-card__icon--cyan">
              <i className="bi bi-hourglass-split" aria-hidden />
            </span>
            <div>
              <p className="user-profile-stat-card__value">{leaveSummary.daysRemaining}</p>
              <p className="user-profile-stat-card__label">
                {t("app.users.statDaysRemaining", "Days remaining")}
              </p>
            </div>
          </article>
        </div>
      ) : null}

      <div className="user-profile-grid">
        <section className="user-profile-panel">
          <header className="user-profile-panel__header">
            <i className="bi bi-person-vcard" aria-hidden />
            <h3>{t("app.users.personalInformation", "Personal & workplace")}</h3>
          </header>
          <dl className="user-profile-details">
            <div className="user-profile-detail">
              <dt>
                <i className="bi bi-building" aria-hidden /> {t("app.users.tableHeaderWorkplace", "Workplace")}
              </dt>
              <dd>{profile.office}</dd>
            </div>
            <div className="user-profile-detail">
              <dt>
                <i className="bi bi-diagram-3" aria-hidden /> {t("app.users.tableHeaderDepartment", "Department")}
              </dt>
              <dd>{profile.department}</dd>
            </div>
            {profile.designation ? (
              <div className="user-profile-detail">
                <dt>
                  <i className="bi bi-briefcase" aria-hidden /> {t("app.users.designation", "Designation")}
                </dt>
                <dd>{profile.designation}</dd>
              </div>
            ) : null}
            <div className="user-profile-detail">
              <dt>
                <i className="bi bi-calendar-event" aria-hidden />{" "}
                {t("app.users.employmentStartDate", "Employment start")}
              </dt>
              <dd>{profile.employmentStartDate || t("app.users.notSpecified", "Not specified")}</dd>
            </div>
            {profile.accountCreated ? (
              <div className="user-profile-detail">
                <dt>
                  <i className="bi bi-clock-history" aria-hidden /> {t("app.users.accountCreated", "Account created")}
                </dt>
                <dd>{profile.accountCreated}</dd>
              </div>
            ) : null}
            <div className="user-profile-detail">
              <dt>
                <i className="bi bi-person-check" aria-hidden /> {t("app.users.leaveApprover", "Leave approver")}
              </dt>
              <dd>{profile.leaveApprover}</dd>
            </div>
          </dl>
        </section>

        <section className="user-profile-panel user-profile-panel--wide">
          <header className="user-profile-panel__header">
            <i className="bi bi-calendar2-week" aria-hidden />
            <h3>{t("app.users.leaveTypes", "Leave entitlements")}</h3>
          </header>

          {leaveTypes.length === 0 ? (
            <p className="user-profile-leave-empty">
              {t("app.users.noLeaveTypes", "No active leave types configured for this employee.")}
            </p>
          ) : (
            <div className="user-profile-leave-grid">
              {leaveTypes.map((leaveType, index) => {
                const name = leaveType.name || t("app.users.unknownLeaveType", "Leave");
                const total = leaveType.totalAllowance || leaveType.value || 0;
                const used = leaveType.usedLeaves || 0;
                const remaining = leaveType.remainingLeaves ?? Math.max(0, total - used);
                const color = leaveType.color || accentColor;
                const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;

                return (
                  <article
                    key={leaveType.leaveBalanceId || leaveType.leaveTypeId || index}
                    className="user-profile-leave-card"
                    style={{ "--leave-color": color }}
                  >
                    <div className="user-profile-leave-card__head">
                      <h4>{name}</h4>
                      {leaveType.icon && leaveType.icon !== "no-icon" ? (
                        <Icon name={leaveType.icon} style={{ color }} />
                      ) : (
                        <i className="bi bi-circle-fill" style={{ color, fontSize: "0.5rem" }} aria-hidden />
                      )}
                    </div>
                    <div className="user-profile-leave-card__bar">
                      <div className="user-profile-leave-card__bar-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="user-profile-leave-card__metrics">
                      <div>
                        <span className="user-profile-leave-card__num">{total}</span>
                        <span className="user-profile-leave-card__meta">{t("app.users.leaveTotal", "Total")}</span>
                      </div>
                      <div>
                        <span className="user-profile-leave-card__num user-profile-leave-card__num--used">
                          {used}
                        </span>
                        <span className="user-profile-leave-card__meta">{t("app.users.leaveUsed", "Used")}</span>
                      </div>
                      <div>
                        <span className="user-profile-leave-card__num user-profile-leave-card__num--remaining">
                          {remaining}
                        </span>
                        <span className="user-profile-leave-card__meta">
                          {t("app.users.leaveRemaining", "Remaining")}
                        </span>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {hasPersonalDetails || hasContactDetails || hasIdentification || hasAddress ? (
        <div className="user-profile-grid user-profile-grid--secondary">
          {hasPersonalDetails ? (
            <section className="user-profile-panel">
              <header className="user-profile-panel__header">
                <i className="bi bi-person-lines-fill" aria-hidden />
                <h3>{t("app.users.personalDetailsSubsection", "Personal Details")}</h3>
              </header>
              <dl className="user-profile-details">
                <ProfileDetail icon="upc" label={t("app.users.employeeCode", "Employee Code / Enroll No.")} value={ap.employeeCode} />
                <ProfileDetail icon="gender-ambiguous" label={t("app.users.gender", "Gender")} value={ap.gender} />
                <ProfileDetail icon="balloon" label={t("app.users.dateOfBirth", "Date of Birth")} value={ap.dateOfBirth} />
                <ProfileDetail icon="heart" label={t("app.users.maritalStatus", "Marital Status")} value={ap.maritalStatus} />
                <ProfileDetail
                  icon="person"
                  label={t("app.users.fatherOrHusbandName", "Father / Husband Name")}
                  value={ap.fatherOrHusbandName}
                />
                <ProfileDetail icon="droplet" label={t("app.users.bloodGroup", "Blood Group")} value={ap.bloodGroup} />
                <ProfileDetail
                  icon="universal-access"
                  label={t("app.users.disability", "Disability")}
                  value={ap.disability}
                />
              </dl>
            </section>
          ) : null}

          {hasContactDetails ? (
            <section className="user-profile-panel">
              <header className="user-profile-panel__header">
                <i className="bi bi-telephone" aria-hidden />
                <h3>{t("app.users.contactDetailsSubsection", "Contact Details")}</h3>
              </header>
              <dl className="user-profile-details">
                <ProfileDetail icon="telephone" label={t("app.users.phoneNumber", "Phone Number")} value={ap.phoneNumber} />
                <ProfileDetail
                  icon="person-exclamation"
                  label={t("app.users.emergencyContactName", "Emergency Contact Name")}
                  value={ap.emergencyContactName}
                />
                <ProfileDetail
                  icon="telephone-plus"
                  label={t("app.users.emergencyContactNumber", "Emergency Contact Number")}
                  value={ap.emergencyContactNumber}
                />
              </dl>
            </section>
          ) : null}

          {hasIdentification ? (
            <section className="user-profile-panel">
              <header className="user-profile-panel__header">
                <i className="bi bi-card-heading" aria-hidden />
                <h3>{t("app.users.identificationSubsection", "Identification")}</h3>
              </header>
              <dl className="user-profile-details">
                <ProfileDetail icon="person-vcard" label={t("app.users.nationalId", "National ID")} value={ap.cnic} />
                <ProfileDetail
                  icon="airplane"
                  label={t("app.users.passportNumber", "Passport Number")}
                  value={ap.passportNumber}
                />
                <ProfileDetail icon="receipt" label={t("app.users.taxNumber", "Tax Number")} value={ap.ntn} />
              </dl>
            </section>
          ) : null}

          {hasAddress ? (
            <section className="user-profile-panel">
              <header className="user-profile-panel__header">
                <i className="bi bi-geo-alt" aria-hidden />
                <h3>{t("app.users.addressDetailsSubsection", "Address")}</h3>
              </header>
              <dl className="user-profile-details">
                <ProfileDetail
                  icon="house"
                  label={t("app.users.residentialAddress", "Current Address")}
                  value={ap.residentialAddress}
                />
                <ProfileDetail
                  icon="house-heart"
                  label={t("app.users.permanentAddress", "Permanent Address")}
                  value={ap.permanentAddress}
                />
                <ProfileDetail icon="buildings" label={t("app.users.city", "City")} value={ap.city} />
                <ProfileDetail icon="globe-americas" label={t("app.users.country", "Country")} value={ap.country} />
                <ProfileDetail icon="mailbox" label={t("app.users.postalCode", "Postal Code")} value={ap.postalCode} />
              </dl>
            </section>
          ) : null}
        </div>
      ) : null}

      {hasDependents ? (
        <section className="user-profile-panel user-profile-panel--dependents">
          <header className="user-profile-panel__header">
            <i className="bi bi-people" aria-hidden />
            <h3>{t("app.users.dependentsSubsection", "Dependents")}</h3>
          </header>
          <div className="user-profile-dependents">
            {ap.dependents.map((dependent, index) => (
              <article className="user-profile-dependent-card" key={`${dependent.name}-${index}`}>
                <h4>{dependent.name}</h4>
                <dl className="user-profile-details">
                  <ProfileDetail
                    icon="diagram-2"
                    label={t("app.users.dependentRelationship", "Relationship")}
                    value={dependent.relationship}
                  />
                  <ProfileDetail
                    icon="balloon"
                    label={t("app.users.dependentDateOfBirth", "Date of Birth")}
                    value={dependent.dob}
                  />
                  <ProfileDetail
                    icon="gender-ambiguous"
                    label={t("app.users.dependentGender", "Gender")}
                    value={dependent.gender}
                  />
                  <ProfileDetail
                    icon="telephone"
                    label={t("app.users.dependentContactNumber", "Contact Number")}
                    value={dependent.contactNumber}
                  />
                </dl>
              </article>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default UserProfilePage;
